import React, { useEffect, useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import dayjs from 'dayjs';
import { db } from '@root/firebase';
import { collection, getDocs, orderBy, query, where } from 'firebase/firestore';

/**
 * SevenDays (Click-throughs)
 *
 * Props:
 * - uid: string (required) -> owner uid to filter your marketplace items
 * - kind: 'products' | 'adverts' (required)
 * - title?: string -> heading shown above chart
 * - maxSeries?: number -> max number of lines to draw (default 4)
 * - height?: number -> chart height (default 360)
 * - itemLabelField?: string -> field to use for series label, defaults by kind:
 *      products -> 'product'
 *      adverts  -> 'title'
 * - subcollectionName?: string -> defaults to 'referrals'
 */
export default function SevenDays({
  uid,
  kind,
  title,
  maxSeries = 4,
  height = 360,
  itemLabelField,
  subcollectionName = 'referrals',
}) {
  const [labels, setLabels] = useState([]);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sum7d, setSum7d] = useState(0);

  const labelField =
    itemLabelField ?? (kind === 'adverts' ? 'title' : 'product');

  // ---------- helpers ----------
  const makeLast7DayBuckets = () => {
    // oldest → newest
    const days = Array.from({ length: 7 }, (_, i) =>
      dayjs()
        .startOf('day')
        .subtract(6 - i, 'day')
    );
    const keys = days.map((d) => d.format('YYYY-MM-DD'));
    const lbls = days.map((d) => d.format('ddd D')); // e.g., Wed 15
    return { bucketKeys: keys, labels: lbls };
  };

  const trimName = (s = '', max = 28) =>
    s.length > max ? s.slice(0, max - 1) + '…' : s;

  // ---------- fetch ----------
  useEffect(() => {
    if (!uid || !kind) return;

    const run = async () => {
      setLoading(true);
      try {
        // 1) fetch your marketplace items by kind
        const itemsQ = query(
          collection(db, 'marketplace'),
          where('type', '==', kind),
          where('uid', '==', uid)
        );
        const itemsSnap = await getDocs(itemsQ);
        const items = itemsSnap.docs.map((d) => ({
          id: d.id,
          label:
            d.data()?.[labelField] ||
            d.data()?.title ||
            d.data()?.product ||
            '(untitled)',
        }));

        const { bucketKeys, labels } = makeLast7DayBuckets();
        setLabels(labels);

        const since = dayjs().startOf('day').subtract(6, 'day').toDate();

        // 2) build per-item 7d counts from subcollection (referrals)
        const perItem = await Promise.all(
          items.map(async (it) => {
            const refQ = query(
              collection(db, 'marketplace', it.id, subcollectionName),
              where('createdAt', '>=', since),
              orderBy('createdAt', 'asc')
            );
            const snap = await getDocs(refQ);

            const counts = new Map(bucketKeys.map((k) => [k, 0]));
            snap.forEach((r) => {
              const ts = r.data()?.createdAt;
              const dt = ts?.toDate?.()
                ? ts.toDate()
                : ts
                ? new Date(ts)
                : null;
              if (!dt) return;
              const k = dayjs(dt).format('YYYY-MM-DD');
              if (counts.has(k)) counts.set(k, (counts.get(k) || 0) + 1);
            });

            const data = bucketKeys.map((k) => counts.get(k) || 0);
            const total = data.reduce((a, b) => a + b, 0);
            return { ...it, data, total };
          })
        );

        // 3) pick top N by total, keep at least first few when all zero
        const sorted = perItem.sort((a, b) => b.total - a.total);
        const chosen = sorted.some((x) => x.total > 0)
          ? sorted.filter((x) => x.total > 0).slice(0, maxSeries)
          : sorted.slice(0, Math.min(maxSeries, sorted.length));

        setSeries(
          chosen.map((it) => ({
            name: `${trimName(it.label)} (${it.total})`,
            data: it.data,
          }))
        );

        setSum7d(perItem.reduce((s, it) => s + it.total, 0));
      } catch (e) {
        console.error('[SevenDays] load error', e);
        setSeries([]);
        setSum7d(0);
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [uid, kind, labelField, maxSeries, subcollectionName]);

  // ---------- chart options ----------
  const options = useMemo(
    () => ({
      chart: {
        id: `seven-days-${kind}`,
        toolbar: { show: false },
        animations: { easing: 'easeinout', speed: 300 },
      },
      stroke: { curve: 'smooth', width: 3 },
      dataLabels: { enabled: false },
      xaxis: {
        categories: labels,
        labels: { rotate: -15 },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        labels: { formatter: (v) => Math.round(v) },
        min: 0,
        forceNiceScale: true,
      },
      tooltip: { shared: true, intersect: false, theme: 'light' },
      legend: {
        show: true,
        position: 'top',
        horizontalAlign: 'left',
        markers: { radius: 2 },
      },
      grid: { strokeDashArray: 4 },
    }),
    [labels, kind]
  );

  const heading =
    title ||
    `Click-throughs by ${
      kind === 'adverts' ? 'Advert' : 'Product'
    } (Last 7 Days)`;

  return (
    <div className='card'>
      <div className='card-header d-flex justify-content-between align-items-center'>
        <h5 className='mb-0'>{heading}</h5>
        <small className='text-muted'>
          {loading ? 'Loading…' : `Total: ${sum7d}`}
        </small>
      </div>
      <div className='card-body'>
        <ReactApexChart
          type='line'
          height={height}
          options={options}
          series={
            series.length
              ? series
              : [{ name: 'No data', data: [0, 0, 0, 0, 0, 0, 0] }]
          }
        />
      </div>
    </div>
  );
}
